import { apiClient } from './apiClient';

export interface GPSLocation {
  latitude: number;
  longitude: number;
}

/**
 * Coerce an untyped stored value (e.g. a job site's `gps_location`, typed
 * `unknown` off the API) into a GPSLocation, or null if it isn't a valid
 * lat/lng pair. Used to feed the property's saved location into the location
 * picker as a fallback map center.
 */
export function readGpsLocation(value: unknown): GPSLocation | null {
  const loc = value as { latitude?: unknown; longitude?: unknown } | null;
  if (loc && typeof loc.latitude === 'number' && typeof loc.longitude === 'number') {
    return { latitude: loc.latitude, longitude: loc.longitude };
  }
  return null;
}

let cachedApiKey: string | null = null;
let apiKeyFetched = false;

export async function getGoogleMapsApiKey(): Promise<string | null> {
  if (apiKeyFetched) return cachedApiKey;
  try {
    const { value } = await apiClient.get<{ key: string; value: string | null }>(
      '/api/app-settings/public/google_maps_api_key',
    );
    cachedApiKey = value || null;
  } catch {
    cachedApiKey = null;
  }
  apiKeyFetched = true;
  return cachedApiKey;
}

export function clearApiKeyCache(): void {
  cachedApiKey = null;
  apiKeyFetched = false;
}

let scriptLoading = false;
let scriptEl: HTMLScriptElement | null = null;
let pending: { resolve: () => void; reject: (err: Error) => void }[] = [];

/** How long we wait for the third-party script before giving up and showing a
 *  message. Without this a blocked/hung request left every map stuck on
 *  "Loading map…" forever, with no retry ever attempted. */
const LOAD_TIMEOUT_MS = 20000;

declare global {
  interface Window {
    google?: typeof google;
    initGoogleMapsCallback?: () => void;
  }
}

export type GoogleMapsFailureReason = 'no-api-key' | 'unavailable';

export class GoogleMapsLoadError extends Error {
  readonly reason: GoogleMapsFailureReason;
  constructor(reason: GoogleMapsFailureReason, message: string) {
    super(message);
    this.name = 'GoogleMapsLoadError';
    this.reason = reason;
  }
}

/**
 * The ONLY sanctioned way to reach the Maps API.
 *
 * Every `google.maps.*` reference in the app must go through this, because a
 * bare `google` identifier is a hard ReferenceError ("Can't find variable:
 * google" in Safari) when the script isn't there — which took down whole
 * screens via the error boundary. A module-level "we started the script" flag
 * is NOT proof the global exists: the request can be blocked by a content
 * blocker, stubbed by an extension that politely calls our `callback` with no
 * API installed, or time out. So we ask the global itself, every time.
 *
 * Returns the `google.maps` namespace, or null when it genuinely isn't there.
 */
export function getGoogleMapsApi(): typeof google.maps | null {
  if (typeof window === 'undefined') return null;
  const g = window.google;
  return g && g.maps && typeof g.maps.Map === 'function' ? g.maps : null;
}

export function isGoogleMapsLoaded(): boolean {
  return getGoogleMapsApi() !== null;
}

export async function loadGoogleMaps(): Promise<void> {
  const apiKey = await getGoogleMapsApiKey();
  if (!apiKey) {
    throw new GoogleMapsLoadError(
      'no-api-key',
      'Google Maps API key not configured. Set it up in Settings > App Settings.',
    );
  }
  return loadGoogleMapsScript(apiKey);
}

function loadGoogleMapsScript(apiKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (isGoogleMapsLoaded()) {
      resolve();
      return;
    }
    pending.push({ resolve, reject });
    if (scriptLoading) return;
    scriptLoading = true;

    let timer: number | undefined;
    let settled = false;

    // Settle every waiter exactly once. On failure we deliberately reset the
    // loading latch and drop the script element so a later mount can retry —
    // the previous version latched `scriptLoading` forever on a failed load.
    // `settled` also stops a late error/callback from THIS attempt clobbering a
    // retry that has already started.
    const finish = (err: Error | null) => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) {
        window.clearTimeout(timer);
        timer = undefined;
      }
      scriptLoading = false;
      const waiters = pending;
      pending = [];
      if (err) {
        scriptEl?.remove();
        scriptEl = null;
        waiters.forEach((w) => w.reject(err));
      } else {
        waiters.forEach((w) => w.resolve());
      }
    };

    const unavailable = () =>
      new GoogleMapsLoadError('unavailable', 'Google Maps could not be loaded.');

    window.initGoogleMapsCallback = () => {
      // Being called back is not proof the API is installed — verify.
      finish(isGoogleMapsLoaded() ? null : unavailable());
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initGoogleMapsCallback&libraries=marker,geocoding`;
    script.async = true;
    script.defer = true;
    script.onerror = () => finish(unavailable());
    scriptEl = script;

    timer = window.setTimeout(() => finish(unavailable()), LOAD_TIMEOUT_MS);
    document.head.appendChild(script);
  });
}

export async function geocodeAddress(address: string): Promise<GPSLocation | null> {
  // A missing API key still throws — that's a configuration problem an admin
  // needs to see, and callers already handled it. A script that won't load is
  // not actionable by the user, so it degrades to this function's existing
  // "null = couldn't resolve it" contract instead of becoming a new rejection
  // that callers were never written to expect.
  try {
    await loadGoogleMaps();
  } catch (err) {
    if (err instanceof GoogleMapsLoadError && err.reason === 'no-api-key') throw err;
    console.error('Geocoding unavailable — Google Maps failed to load:', err);
    return null;
  }
  const maps = getGoogleMapsApi();
  if (!maps) return null;

  return new Promise((resolve) => {
    const geocoder = new maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status === maps.GeocoderStatus.OK && results && results.length > 0) {
        const location = results[0].geometry.location;
        resolve({ latitude: location.lat(), longitude: location.lng() });
      } else {
        resolve(null);
      }
    });
  });
}
